import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { StoreInterface } from '../../store/types';
import { actionCreators } from '../../store/actions';
import { readCookieConsent } from '../../utils/cookieConsent';

/**
 * Re-reads the consent cookie on every route, and whenever the tab comes back into view or focus, and brings
 * the persisted redux copy (`appStatus.cookiesConsent`) in line with it. The banner that refreshes the copy
 * (CookieConsent) is mounted on RestaurantDetailsScreen only, and the copy can outlive the cookie (see
 * readCookieConsent), so a customer landing on any other route (the basket, checkout, an order page, a
 * payment return) would otherwise run on a stale copy. The copy is not synced across tabs, so an answer
 * given in another tab is picked up when the customer returns to this one. What reads the copy follows it:
 * AnalyticsProvider re-reads the cookie for the Mixpanel consent when the copy changes, MarketingProvider
 * holds the app-download modal back until the banner has an answer.
 */
export default function CookieConsentSync() {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const persistedConsent = useSelector((state: StoreInterface) => state.appStatus.cookiesConsent);
  const persistedConsentRef = useRef(persistedConsent);
  persistedConsentRef.current = persistedConsent;

  const syncConsent = () => {
    const consent = readCookieConsent();

    if (consent !== persistedConsentRef.current) {
      dispatch(actionCreators.appStatus.setCookieConsent(consent));
    }
  };

  useEffect(() => {
    syncConsent();
  }, [pathname]);

  useEffect(() => {
    const syncWhenVisible = () => document.visibilityState === 'visible' && syncConsent();

    window.addEventListener('focus', syncConsent);
    document.addEventListener('visibilitychange', syncWhenVisible);

    return () => {
      window.removeEventListener('focus', syncConsent);
      document.removeEventListener('visibilitychange', syncWhenVisible);
    };
  }, []);

  return null;
}
